import { useCallback } from 'react';
import { ApolloError } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import {
  ErpFundingMailSettingsDocument,
  ErpFundingMailSettingsQuery,
  FundingMailRuleInput,
  FundingMailSettingsInput,
  useErpDeleteFundingMailRuleMutation,
  useErpFundingMailCalendarQuery,
  useErpFundingMailRulesQuery,
  useErpFundingMailSendgridTemplatesQuery,
  useErpFundingMailSettingsQuery,
  useErpFundingMailTemplateCheckQuery,
  useErpFundingMailVariablesQuery,
  useErpSaveFundingMailRuleMutation,
  useErpSaveFundingMailSettingsMutation,
  useErpSendFundingMailTestMutation,
} from 'core/graphql';
import { ILabel, labelText } from 'core/utils/lmsSuspension';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useStableRefetch } from '../Groups/useStableRefetch';
import { useToasts } from '../Toast/useToasts';
import {
  IFundingMailCalendarEntry,
  IFundingMailRule,
  IFundingMailSettings,
  IFundingMailTemplateCheck,
  IFundingMailVariable,
  MAIL_CALENDAR_DAYS,
  MAIL_ERROR_FORBIDDEN_KEY,
  MAIL_RULES_NS,
  mailErrorLabel,
} from './mailRulesModel';
import { useDofinPermissions } from './useDofinPermissions';

/**
 * ТЗ 0085 — «Правила писем» дофинансирования: данные и запись.
 *
 * Читают те же роли, что весь раздел (`canRead`): без права запрос не
 * уходит вовсе — `PermissionDenied` отклоняет промис и лёг бы ошибкой поверх
 * чужого экрана. Правит тот, кому сервер ответил `canEdit` — то же правило,
 * что у мутаций (`DOFIN_MANAGER_ROLES`), а не вторая копия ролей на клиенте.
 *
 * Стабильные пустые массивы: свежий `[]` на каждый рендер перезапускает
 * зависимые `useMemo`/`useEffect`.
 */
const EMPTY_RULES: IFundingMailRule[] = [];
const EMPTY_VARIABLES: IFundingMailVariable[] = [];
const EMPTY_CALENDAR: IFundingMailCalendarEntry[] = [];
const EMPTY_TEMPLATES: ISendgridTemplateSummary[] = [];

export interface ISendgridTemplateSummary {
  id: string;
  name: string;
  subject?: string | null;
}

export const useFundingMailSettings = () => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error, refetch } = useErpFundingMailSettingsQuery({
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });
  return {
    settings: (data?.fundingMailSettings ??
      null) as IFundingMailSettings | null,
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

export const useFundingMailRules = () => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error, refetch } = useErpFundingMailRulesQuery({
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });
  return {
    rules: (data?.fundingMailRules ?? EMPTY_RULES) as IFundingMailRule[],
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

export const useFundingMailVariables = () => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error } = useErpFundingMailVariablesQuery({
    skip: !canRead,
    // Справочник меняется только с выкладкой сервера.
    fetchPolicy: 'cache-first',
  });
  return {
    variables: (data?.fundingMailVariables ??
      EMPTY_VARIABLES) as IFundingMailVariable[],
    loading,
    error: error as ApolloError | undefined,
  };
};

/**
 * Шаблоны SendGrid — только когда окно выбора открыто: это живой запрос в
 * SendGrid, а не в нашу базу, и таблице правил он не нужен.
 *
 * Отказ SendGrid — `errorCode`, а не пустой список: «шаблонов нет» и
 * «SendGrid не ответил» — разные сообщения.
 */
export const useFundingMailSendgridTemplates = (enabled: boolean) => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error } = useErpFundingMailSendgridTemplatesQuery({
    skip: !canRead || !enabled,
    fetchPolicy: 'cache-and-network',
  });
  const payload = data?.fundingMailSendgridTemplates;
  const code = payload?.success
    ? null
    : payload?.customErrors?.find((e) => e?.code)?.code ?? null;
  return {
    templates: (payload?.templates ??
      EMPTY_TEMPLATES) as ISendgridTemplateSummary[],
    errorCode: code,
    loading,
    error: error as ApolloError | undefined,
  };
};

/**
 * Переменные шаблона против тех, что передаёт робот. Кешируется по id
 * шаблона: у двух правил с одним шаблоном — один запрос в SendGrid.
 */
export const useFundingMailTemplateCheck = (templateId?: string | null) => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error } = useErpFundingMailTemplateCheckQuery({
    variables: { templateId: templateId || '' },
    skip: !canRead || !templateId,
    fetchPolicy: 'cache-and-network',
  });
  return {
    check: (data?.fundingMailTemplateCheck ??
      null) as IFundingMailTemplateCheck | null,
    loading,
    error: error as ApolloError | undefined,
  };
};

export const useFundingMailCalendar = (days: number = MAIL_CALENDAR_DAYS) => {
  const { canRead } = useDofinPermissions();
  const { data, loading, error, refetch } = useErpFundingMailCalendarQuery({
    variables: { days },
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });
  return {
    entries: (data?.fundingMailCalendar ??
      EMPTY_CALENDAR) as IFundingMailCalendarEntry[],
    loading,
    error: error as ApolloError | undefined,
    refetch: useStableRefetch(refetch),
  };
};

// ---------------------------------------------------------------------------
// Запись
// ---------------------------------------------------------------------------

export interface IMailActionError {
  /** Поле сервера (`offset_days`, `template_id`, …) — под каким полем печатать. */
  field?: string | null;
  label: ILabel;
}

export interface IMailActionResult {
  ok: boolean;
  errors: IMailActionError[];
  /** Сохранённое правило — вызывающему, например, чтобы узнать его id. */
  rule?: IFundingMailRule | null;
  /** Куда ушло тестовое письмо. */
  sentTo?: string | null;
}

export interface IMailActionOptions {
  /**
   * Ошибку покажет сам вызывающий — под полем в окне. Без этого флага она
   * поднимается тостом: у переключателя в таблице своего места для текста нет.
   */
  inline?: boolean;
}

type ErrList =
  | Array<{ field?: string | null; code?: string | null } | null>
  | null
  | undefined;

const toErrors = (list: ErrList): IMailActionError[] => {
  const errors = (list ?? [])
    .filter(Boolean)
    .map((e) => ({ field: e?.field ?? null, label: mailErrorLabel(e?.code) }));
  return errors.length ? errors : [{ field: null, label: mailErrorLabel() }];
};

/**
 * Отказ прав — не `customErrors`: `PermissionDenied` приходит в GraphQL
 * `errors` и ОТКЛОНЯЕТ промис. Текст `permissions_checker` — единственный
 * признак, доезжающий до клиента.
 */
const rejectionErrors = (error: unknown): IMailActionError[] => {
  const message = (error as ApolloError)?.graphQLErrors?.[0]?.message || '';
  return [
    {
      field: null,
      label: message.toLowerCase().includes('permission')
        ? { key: MAIL_ERROR_FORBIDDEN_KEY }
        : mailErrorLabel(),
    },
  ];
};

export const useFundingMailActions = () => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [
    saveSettingsMutation,
    settingsState,
  ] = useErpSaveFundingMailSettingsMutation();
  const [saveRuleMutation, ruleState] = useErpSaveFundingMailRuleMutation();
  const [
    deleteRuleMutation,
    deleteState,
  ] = useErpDeleteFundingMailRuleMutation();
  const [sendTestMutation, testState] = useErpSendFundingMailTestMutation();

  const notify = useCallback(
    (type: ToastTypes, title: string, text?: string) =>
      addToast({
        data: { type, title, text },
        type: GlobalToastTypeNames.Dofinansowanie,
      }),
    [addToast],
  );

  /** Итог записи: успех — тост; отказ — тост или строка под полем. */
  const finish = useCallback(
    (
      result: IMailActionResult,
      successKey: string | null,
      options?: IMailActionOptions,
    ): IMailActionResult => {
      if (result.ok) {
        if (successKey) notify(ToastTypes.success, t(successKey));
      } else if (!options?.inline) {
        notify(
          ToastTypes.error,
          t(`${MAIL_RULES_NS}.toast.failed`),
          result.errors.map((e) => labelText(e.label, t)).join('\n'),
        );
      }
      return result;
    },
    [notify, t],
  );

  const saveSettings = useCallback(
    async (
      input: FundingMailSettingsInput,
      options?: IMailActionOptions,
    ): Promise<IMailActionResult> => {
      try {
        const { data } = await saveSettingsMutation({
          variables: { input },
          // У настроек нет id — нормализованный кеш их не обновит сам.
          // Пишем ответ в запрос экрана, чтобы блок «Состояние» не ждал
          // второго похода на сервер.
          update: (cache, { data: saved }) => {
            const mailSettings = saved?.saveFundingMailSettings?.mailSettings;
            if (!mailSettings) return;
            cache.writeQuery<ErpFundingMailSettingsQuery>({
              query: ErpFundingMailSettingsDocument,
              data: { fundingMailSettings: mailSettings },
            });
          },
        });
        const payload = data?.saveFundingMailSettings;
        return finish(
          payload?.success
            ? { ok: true, errors: [] }
            : { ok: false, errors: toErrors(payload?.customErrors) },
          `${MAIL_RULES_NS}.toast.settingsSaved`,
          options,
        );
      } catch (error) {
        return finish(
          { ok: false, errors: rejectionErrors(error) },
          null,
          options,
        );
      }
    },
    [finish, saveSettingsMutation],
  );

  const saveRule = useCallback(
    async (
      input: FundingMailRuleInput,
      options?: IMailActionOptions,
    ): Promise<IMailActionResult> => {
      try {
        const { data } = await saveRuleMutation({ variables: { input } });
        const payload = data?.saveFundingMailRule;
        return finish(
          payload?.success
            ? {
                ok: true,
                errors: [],
                rule: (payload.rule ?? null) as IFundingMailRule | null,
              }
            : { ok: false, errors: toErrors(payload?.customErrors) },
          `${MAIL_RULES_NS}.toast.ruleSaved`,
          options,
        );
      } catch (error) {
        return finish(
          { ok: false, errors: rejectionErrors(error) },
          null,
          options,
        );
      }
    },
    [finish, saveRuleMutation],
  );

  const deleteRule = useCallback(
    async (id: string): Promise<IMailActionResult> => {
      try {
        const { data } = await deleteRuleMutation({ variables: { id } });
        const payload = data?.deleteFundingMailRule;
        return finish(
          payload?.success
            ? { ok: true, errors: [] }
            : { ok: false, errors: toErrors(payload?.customErrors) },
          `${MAIL_RULES_NS}.toast.ruleDeleted`,
        );
      } catch (error) {
        return finish({ ok: false, errors: rejectionErrors(error) }, null);
      }
    },
    [deleteRuleMutation, finish],
  );

  /** Тест не пишет в журнал — поэтому и тоста «сохранено» у него нет. */
  const sendTest = useCallback(
    async (
      ruleId: string,
      intakeId?: string | null,
      templateId?: string | null,
    ): Promise<IMailActionResult> => {
      try {
        const { data } = await sendTestMutation({
          variables: {
            ruleId,
            intakeId: intakeId || undefined,
            templateId: templateId || undefined,
          },
        });
        const payload = data?.sendFundingMailTest;
        return finish(
          payload?.success
            ? { ok: true, errors: [], sentTo: payload.sentTo ?? null }
            : { ok: false, errors: toErrors(payload?.customErrors) },
          null,
          { inline: true },
        );
      } catch (error) {
        return finish({ ok: false, errors: rejectionErrors(error) }, null, {
          inline: true,
        });
      }
    },
    [finish, sendTestMutation],
  );

  return {
    saveSettings,
    saveRule,
    deleteRule,
    sendTest,
    savingSettings: settingsState.loading,
    savingRule: ruleState.loading,
    deleting: deleteState.loading,
    testing: testState.loading,
  };
};
